import { zodResolver } from "@hookform/resolvers/zod"
import {
  createTeamSpace,
  TeamSpace,
  type TeamSpaceUpsert,
  updateTeamSpace,
  useInvalidateTeamSpaces,
} from "@prismicio/editor-fields"
import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  Form,
  HeaderTabLink,
  useToast,
} from "@prismicio/editor-ui"
import { useMutation } from "@tanstack/react-query"
import { useId, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import * as z from "zod"

import { env } from "../../app/env"
import { getRepositoryName } from "../../app/routing"
import { mainSpace, useTeamSpaces } from "../../app/services/role"
import {
  trackTeamSpaceCreated,
  trackTeamSpaceUpdated,
} from "../../app/services/tracking"
import { useTeamSpaceNavigation } from "./TeamSpaces.utils"
import { TeamSpacesDialogGeneralTab } from "./TeamSpacesDialogGeneralTab"
import { TeamSpacesDialogUserTab } from "./TeamSpacesDialogUserTab"

interface TeamSpacesDialogProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  teamSpace?: TeamSpace
}

export function TeamSpacesDialog(props: TeamSpacesDialogProps) {
  const { isOpen, setIsOpen, teamSpace } = props

  function closeSettings() {
    setIsOpen(false)
  }

  const { mutate: createTeamSpace, isPending: isCreating } =
    useCreateTeamSpaceMutation({
      onSuccess: closeSettings,
    })

  const { mutate: updateTeamSpace, isPending: isUpdating } =
    useUpdateTeamSpaceMutation({
      onSuccess: closeSettings,
    })

  const isLoading = isCreating || isUpdating

  return (
    <Dialog
      onOpenChange={(open) => {
        if (isLoading) return
        setIsOpen(open)
      }}
      open={isOpen}
      closeButtonDisabled={isLoading}
      noDescription
    >
      <TeamSpacesDialogContent
        closeSettings={closeSettings}
        teamSpace={teamSpace}
        isLoading={isLoading}
        createTeamSpace={createTeamSpace}
        updateTeamSpace={updateTeamSpace}
      />
    </Dialog>
  )
}

interface UseTeamSpaceFormSchemaArgs {
  teamSpace?: TeamSpace
}

function useTeamSpaceFormSchema(args: UseTeamSpaceFormSchemaArgs) {
  const { teamSpace } = args

  const teamSpaces = useTeamSpaces()

  return useMemo(() => {
    const teamSpaceNames = teamSpaces.map((teamSpace) => teamSpace.name)

    return z.object({
      name: z
        .string()
        .trim()
        .min(1, "This field is required")
        .max(50)
        .refine(
          // Disallow duplicate names, but allow the same name when editing
          (name) => !teamSpaceNames.includes(name) || teamSpace?.name === name,
          {
            message: "A Space with this name already exists",
          },
        ),
      customTypes: z.array(z.string()),
    })
  }, [teamSpace, teamSpaces])
}

export type TeamSpaceFormSchema = z.infer<
  ReturnType<typeof useTeamSpaceFormSchema>
>

interface TeamSpacesDialogContentProps {
  teamSpace?: TeamSpace
  isLoading?: boolean
  closeSettings: () => void
  createTeamSpace: (data: TeamSpaceUpsert) => void
  updateTeamSpace: (vars: UpdateTeamSpaceMutationVariables) => void
}

function TeamSpacesDialogContent(props: TeamSpacesDialogContentProps) {
  const {
    teamSpace,
    isLoading,
    closeSettings,
    createTeamSpace,
    updateTeamSpace,
  } = props
  const [tab, setTab] = useState<"general" | "user">("general")

  const TeamSpaceFormSchema = useTeamSpaceFormSchema({ teamSpace })

  const form = useForm<TeamSpaceFormSchema>({
    resolver: zodResolver(TeamSpaceFormSchema),
    defaultValues: {
      name: teamSpace?.name ?? "",
      customTypes: teamSpace?.customTypes ?? [],
    },
  })

  // id used for linking native form and submit button
  const formId = useId()

  return (
    <>
      <DialogHeader title={getDialogTitle(teamSpace)} icon="folderManaged" />
      <DialogContent paddingInline={8} gap={0}>
        <Box gap={8} minHeight={56} position="relative">
          <div
            // one-off 1px bottom-border behind tab links
            style={{
              position: "absolute",
              bottom: 0,
              height: "1px",
              left: "8px",
              right: "8px",
              backgroundColor: "var(--grey7)",
            }}
          />
          <HeaderTabLink
            text="General"
            onClick={() => setTab("general")}
            selected={tab === "general"}
          />
          <HeaderTabLink
            text="User role"
            onClick={() => setTab("user")}
            selected={tab === "user"}
          />
        </Box>
        <FormProvider {...form}>
          <Form
            id={formId}
            sx={{
              marginInline: 8,
              marginBlock: 16,
              // TODO: temporary `height: 100%` to center leads card in User tab
              // to remove after we implement User roles
              height: tab === "user" ? "100%" : "auto",
            }}
            onSubmit={form.handleSubmit((data) => {
              if (teamSpace) {
                updateTeamSpace({
                  teamSpaceId: teamSpace.id,
                  teamSpace: data,
                  oldTeamSpace: teamSpace,
                })
              } else {
                createTeamSpace(data)
              }
            })}
          >
            {tab === "general" && (
              <TeamSpacesDialogGeneralTab
                teamSpace={teamSpace}
                closeSettings={closeSettings}
              />
            )}
            {tab === "user" && <TeamSpacesDialogUserTab />}
          </Form>
        </FormProvider>
      </DialogContent>
      {tab !== "user" && (
        <DialogActions>
          <DialogCancelButton disabled={isLoading} />
          <DialogActionButton
            type="submit"
            form={formId}
            loading={isLoading}
            disabled={!form.formState.isDirty}
          >
            Save
          </DialogActionButton>
        </DialogActions>
      )}
    </>
  )
}

function getDialogTitle(teamSpace?: TeamSpace) {
  if (!teamSpace) return "New Space settings"
  if (teamSpace.id === mainSpace.id) return `${mainSpace.name} settings`
  return `${teamSpace.name} Space settings`
}

interface UseCreateTeamSpaceMutationArgs {
  onSuccess?: () => void
}

function useCreateTeamSpaceMutation(args: UseCreateTeamSpaceMutationArgs) {
  const { onSuccess } = args

  const { showToast } = useToast()

  const invalidateTeamSpaces = useInvalidateTeamSpaces()

  const { navigateToTeamSpace } = useTeamSpaceNavigation()

  return useMutation({
    mutationFn: (teamSpace: TeamSpaceUpsert) =>
      createTeamSpace({
        baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
        repository: getRepositoryName(),
        authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        payload: teamSpace,
      }),
    onError: () => {
      showToast({
        title: "Failed to create a Space",
        icon: "block",
      })
    },
    onSuccess: async (createdTeamSpace) => {
      await invalidateTeamSpaces()

      navigateToTeamSpace(createdTeamSpace.id, createdTeamSpace.customTypes)

      showToast({
        title: "Space created successfully",
      })

      onSuccess?.()

      trackTeamSpaceCreated(createdTeamSpace)
    },
  })
}

type UpdateTeamSpaceMutationVariables = {
  teamSpaceId: string
  teamSpace: TeamSpaceUpsert
  // for tracking purposes
  oldTeamSpace: TeamSpace
}

interface UseUpdateTeamSpaceMutationArgs {
  onSuccess?: () => void
}

function useUpdateTeamSpaceMutation(args: UseUpdateTeamSpaceMutationArgs) {
  const { onSuccess } = args

  const { showToast } = useToast()

  const invalidateTeamSpaces = useInvalidateTeamSpaces()

  const { navigateToTeamSpace } = useTeamSpaceNavigation()

  return useMutation({
    mutationFn: (vars: UpdateTeamSpaceMutationVariables) =>
      updateTeamSpace({
        baseUrl: env.PRISMIC_ROLE_API_BASE_URL,
        repository: getRepositoryName(),
        authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        payload: vars.teamSpace,
        teamSpaceId: vars.teamSpaceId,
      }),
    onError: () => {
      showToast({
        title: "Failed to update a Space",
        icon: "block",
      })
    },
    onSuccess: async (updatedTeamSpace, variables) => {
      await invalidateTeamSpaces()

      navigateToTeamSpace(updatedTeamSpace.id, updatedTeamSpace.customTypes)

      showToast({
        title: "Space updated successfully",
      })

      onSuccess?.()

      trackTeamSpaceUpdated({
        old: variables.oldTeamSpace,
        new: updatedTeamSpace,
      })
    },
  })
}
